import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { api, joinQueue, QUEUE_EVENT } from "@/lib/api";
import { QueueScreen } from "@/components/queue/QueueScreen";

const ACTIVITY_THROTTLE_MS = 30000;

function useActivityPing(enabled) {
  const last = useRef(0);
  const location = useLocation();
  const ping = useCallback(() => {
    const now = Date.now();
    if (now - last.current < ACTIVITY_THROTTLE_MS) return;
    last.current = now;
    api.post("/queue/activity").catch(() => {});
  }, []);
  useEffect(() => {
    if (!enabled) return undefined;
    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, ping, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, ping));
  }, [enabled, ping]);
  useEffect(() => {
    if (enabled) ping();
  }, [enabled, location.pathname, ping]);
}

// Server-enforced virtual queue: renders the public pages only once the visitor holds an active pass.
export const QueueGate = () => {
  const [q, setQ] = useState({ status: "joining" });

  const join = useCallback(async () => {
    try {
      const { data } = await joinQueue();
      setQ(data);
    } catch {
      setQ({ status: "error" });
    }
  }, []);

  useEffect(() => {
    join();
    // Pass lost (idle release / restart): unmount pages, rejoin, then remount so they refetch.
    // A background tab (PWA / TWA) never re-takes a place by itself: it rejoins only once visible again.
    const onLost = () => {
      setQ({ status: "joining" });
      if (document.visibilityState === "visible") return join();
      const onVisible = () => {
        if (document.visibilityState !== "visible") return;
        document.removeEventListener("visibilitychange", onVisible);
        join();
      };
      document.addEventListener("visibilitychange", onVisible);
    };
    window.addEventListener(QUEUE_EVENT, onLost);
    window.addEventListener("online", onLost); // offline -> online: re-take the pass and resume heartbeats
    return () => {
      window.removeEventListener(QUEUE_EVENT, onLost);
      window.removeEventListener("online", onLost);
    };
  }, [join]);

  useEffect(() => {
    if (!["waiting", "error"].includes(q.status)) return undefined;
    const id = setInterval(async () => {
      if (q.status === "error") return join();
      try {
        const { data } = await api.get("/queue/status");
        if (data.status === "expired") join();
        else setQ((prev) => ({ ...prev, ...data }));
      } catch {
        /* keep polling */
      }
    }, Math.min(q.poll_ms || 3000, 5000));
    return () => clearInterval(id);
  }, [q.status, q.poll_ms, join]);

  const allowed = q.status === "active" || q.status === "admin";
  useActivityPing(q.status === "active");

  if (allowed) return <Outlet />;
  return <QueueScreen state={q} onRetry={join} />;
};
